import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { formatApiError } from "../lib/api";
import { Trophy, Loader2 } from "lucide-react";

export default function Register() {
    const { register } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await register(email, password, name);
            nav("/dashboard");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 pt-12 pb-20" data-testid="register-page">
            <div className="flex flex-col items-center mb-8">
                <div className="h-12 w-12 rounded-md bg-[var(--accent-hex)] flex items-center justify-center mb-3">
                    <Trophy className="h-6 w-6 text-[var(--accent-text)]" />
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]">{t("auth.register.title")}</h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{t("auth.register.subtitle")}</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4 p-6 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]">
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.name")}</label>
                    <input
                        required minLength={1} value={name} onChange={(e) => setName(e.target.value)}
                        data-testid="register-name"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.email")}</label>
                    <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        data-testid="register-email"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.password")}</label>
                    <input
                        type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                        data-testid="register-password"
                        className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    />
                </div>
                {err && <p className="text-sm text-[#FF3B30]" data-testid="register-error">{err}</p>}
                <button
                    type="submit" disabled={loading} data-testid="register-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-3 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50 transition-colors"
                >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t("auth.register.submit")}
                </button>
                <p className="text-center text-xs text-[var(--text-secondary)]">
                    {t("auth.have_account")}{" "}
                    <Link to="/login" className="text-[var(--accent-hex)] hover:underline font-bold" data-testid="link-login">{t("auth.login.title")}</Link>
                </p>
            </form>
        </div>
    );
}
